import { useStore } from '../../store';

/**
 * Permission utility functions for checking user permissions.
 * Works with the new permission string format: "{entity}:{action}" (e.g., "material:view", "user:create")
 */

/**
 * Get all permission strings from the user profile.
 * Supports both old format (viewId-based) and new format (action strings).
 * 
 * @param userProfile The user profile from the store
 * @returns Array of permission strings (e.g., ["material:view", "material:create", ...])
 */
export function getUserPermissions(userProfile: { 
  permissions?: Array<{ viewId?: string; read?: boolean; write?: boolean; action?: string }>; 
  superAdmin?: boolean;
  role?: string;
} | null): string[] {
  if (!userProfile) {
    return [];
  }

  // SuperAdmin has all permissions - check both superAdmin flag and role
  if (userProfile.superAdmin || userProfile.role === 'superadmin') {
    return ['*']; // Special permission indicating all permissions
  }

  // If permissions are action strings (new format)
  const permissionStrings = userProfile.permissions
    ?.filter(p => p.action) // Filter for new format
    .map(p => p.action!)
    || [];

  // TODO: Convert old format (viewId-based) to new format if needed
  // For now, we'll work with the new format that the backend will provide

  return permissionStrings;
}

/**
 * Check if the user has a specific permission.
 * 
 * @param permission The permission string to check (e.g., "material:view", "user:create")
 * @param userProfile The user profile from the store
 * @returns true if the user has the permission, false otherwise
 */
export function hasPermission(
  permission: string,
  userProfile: { 
    permissions?: Array<{ viewId?: string; read?: boolean; write?: boolean; action?: string }>; 
    superAdmin?: boolean;
    role?: string;
  } | null
): boolean {
  if (!userProfile || !permission) {
    return false;
  }

  // SuperAdmin has all permissions - check both superAdmin flag and role
  if (userProfile.superAdmin || userProfile.role === 'superadmin') {
    return true;
  }

  // Get all permissions
  const userPermissions = getUserPermissions(userProfile);

  // Check if user has the '*' permission (all permissions)
  if (userPermissions.includes('*')) {
    return true;
  }

  // Check if the specific permission exists
  return userPermissions.includes(permission);
}

/**
 * Hook to check if the current user has a specific permission.
 * 
 * @param permission The permission string to check (e.g., "material:view")
 * @returns true if the user has the permission, false otherwise
 */
export function useHasPermission(permission: string): boolean {
  const userProfile = useStore((state) => state.userProfile);
  return hasPermission(permission, userProfile);
}

/**
 * Check if the user has any of the specified permissions.
 * 
 * @param permissions Array of permission strings to check
 * @param userProfile The user profile from the store
 * @returns true if the user has at least one of the permissions
 */
export function hasAnyPermission(
  permissions: string[],
  userProfile: { 
    permissions?: Array<{ viewId?: string; read?: boolean; write?: boolean; action?: string }>; 
    superAdmin?: boolean;
    role?: string;
  } | null
): boolean {
  if (!permissions || permissions.length === 0) {
    return false;
  }

  return permissions.some(permission => hasPermission(permission, userProfile));
}

/**
 * Check if the user has all of the specified permissions.
 * 
 * @param permissions Array of permission strings to check
 * @param userProfile The user profile from the store
 * @returns true if the user has all of the permissions
 */
export function hasAllPermissions(
  permissions: string[],
  userProfile: { 
    permissions?: Array<{ viewId?: string; read?: boolean; write?: boolean; action?: string }>; 
    superAdmin?: boolean;
    role?: string;
  } | null
): boolean {
  if (!permissions || permissions.length === 0) {
    return false;
  }

  return permissions.every(permission => hasPermission(permission, userProfile));
}

/**
 * Helper to get entity permissions (view, create, edit, delete) for a given entity.
 * 
 * @param entityName The entity name (e.g., "material", "user", "resource")
 * @param userProfile The user profile from the store
 * @returns Object with permission checks for view, create, edit, delete
 */
export function getEntityPermissions(
  entityName: string,
  userProfile: { 
    permissions?: Array<{ viewId?: string; read?: boolean; write?: boolean; action?: string }>; 
    superAdmin?: boolean;
    role?: string;
  } | null
) {
  return {
    canView: hasPermission(`${entityName}:view`, userProfile),
    canCreate: hasPermission(`${entityName}:create`, userProfile),
    canEdit: hasPermission(`${entityName}:edit`, userProfile),
    canDelete: hasPermission(`${entityName}:delete`, userProfile),
  };
}

/**
 * Hook to get entity permissions for a given entity.
 * 
 * @param entityName The entity name (e.g., "material", "user", "resource")
 * @returns Object with permission checks for view, create, edit, delete
 */
export function useEntityPermissions(entityName: string) {
  const userProfile = useStore((state) => state.userProfile);
  return getEntityPermissions(entityName, userProfile);
}

